// FloatingTextLayer — world-space text pops that float up and fade out.
// Drives visual feedback for events that produce a "you just got
// something" moment: capturing a relay (+SIGNAL), neutral camp drops
// (+CACHE), and Core destruction (KO!). Pure presentation (Rule 1).
//
// Snapshot-diff driven, mirroring EffectsLayer / Sfx. Texts are pooled
// Phaser.Text instances; finished texts are recycled.

import type {
  EntityId,
  MatchConnection,
  MatchState,
  PlayerId,
  Unsubscribe,
} from '@signalclash/shared';
import Phaser from 'phaser';

interface FloatingText {
  obj: Phaser.GameObjects.Text;
  bornMs: number;
  durMs: number;
  startX: number;
  startY: number;
  riseDistPx: number;
}

const TEXT_DURATION_MS = 1200;
const RISE_DIST_PX = 56;
// Match-text tint matches the player color so the pop reads as
// "your relay" / "your bonus" without a legend.
const COLOR_OWN = '#a9e0ff';
const COLOR_ENEMY = '#ffb088';
const COLOR_NEUTRAL = '#fff3a0';

export class FloatingTextLayer {
  private prev: MatchState | null = null;
  private unsub: Unsubscribe | null = null;
  private active: FloatingText[] = [];
  private viewerId: PlayerId;

  constructor(private readonly scene: Phaser.Scene, viewerId: PlayerId) {
    this.viewerId = viewerId;
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  attach(connection: MatchConnection): void {
    this.detach();
    this.unsub = connection.onSnapshot((snap) => this.onSnapshot(snap));
  }

  detach(): void {
    this.unsub?.();
    this.unsub = null;
    this.prev = null;
  }

  dispose(): void {
    this.detach();
    for (const t of this.active) t.obj.destroy();
    this.active.length = 0;
  }

  update(nowMs: number): void {
    if (this.active.length === 0) return;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const t = this.active[i];
      if (t === undefined) continue;
      const age = nowMs - t.bornMs;
      if (age >= t.durMs) {
        t.obj.destroy();
        this.active.splice(i, 1);
        continue;
      }
      const k = age / t.durMs;
      // Ease-out cubic for nice deceleration as it rises.
      const eased = 1 - Math.pow(1 - k, 3);
      t.obj.setPosition(t.startX, t.startY - t.riseDistPx * eased);
      t.obj.setAlpha(1 - k * k);
      // Subtle scale-in then steady so it pops on appear.
      const scale = k < 0.18 ? 0.7 + (k / 0.18) * 0.4 : 1.05 - (k - 0.18) * 0.08;
      t.obj.setScale(scale);
    }
  }

  // Public spawner — manual use sites can fire bonus text (Core kill, etc.).
  spawnAt(x: number, y: number, label: string, color: string, opts?: { riseDistPx?: number; durMs?: number; fontSizePx?: number }): void {
    const text = this.scene.add.text(x, y, label, {
      fontFamily: 'Orbitron, ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: `${opts?.fontSizePx ?? 24}px`,
      fontStyle: '900',
      color,
      stroke: '#0a0c10',
      strokeThickness: 4,
      align: 'center',
    });
    text.setOrigin(0.5, 0.5);
    text.setDepth(22);
    text.setShadow(0, 0, color, 8, true, true);
    this.active.push({
      obj: text,
      bornMs: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      durMs: opts?.durMs ?? TEXT_DURATION_MS,
      startX: x,
      startY: y,
      riseDistPx: opts?.riseDistPx ?? RISE_DIST_PX,
    });
  }

  private onSnapshot(snap: MatchState): void {
    if (this.prev === null) {
      this.prev = snap;
      return;
    }
    this.diffCaptures(this.prev, snap);
    this.diffCores(this.prev, snap);
    this.prev = snap;
  }

  // A relay's ownerId flipping to non-null is a capture. We emit a
  // floating "+SIGNAL" at the relay's location tinted by the new owner.
  // Neutralisations (to null) are quieter — no text.
  private diffCaptures(prev: MatchState, next: MatchState): void {
    for (const [idStr, node] of Object.entries(next.controlNodes)) {
      const id = idStr as unknown as EntityId;
      const before = prev.controlNodes[id];
      if (before === undefined) continue;
      if (before.ownerId === node.ownerId) continue;
      if (node.ownerId === null) continue;
      // Show the per-relay income gain (generatesSignal) so the pop
      // teaches the economy as it lands. The "/s" suffix is essential —
      // without it the pop reads as a one-shot grant rather than the
      // ongoing per-second income the relay actually emits.
      const value = node.generatesSignal;
      const color = node.ownerId === this.viewerId ? COLOR_OWN : COLOR_ENEMY;
      const label = `+${value}/s SIGNAL`;
      this.spawnAt(node.pos.x, node.pos.y - 30, label, color);
    }
  }

  // A Core whose HP just hit 0 produces a big KO! pop right at the
  // enemy Core location. Lifecycle 'match_ended' would also fire, but
  // doing this on the snapshot diff guarantees the pop lands ON the
  // exact frame the Core dies — same frame the camera shake punches.
  private diffCores(prev: MatchState, next: MatchState): void {
    for (const [idStr, core] of Object.entries(next.cores)) {
      const id = idStr as unknown as EntityId;
      const before = prev.cores[id];
      if (before === undefined) continue;
      if (before.hp > 0 && core.hp <= 0) {
        const isViewerLoss = core.ownerId === this.viewerId;
        this.spawnAt(
          core.pos.x,
          core.pos.y - 50,
          isViewerLoss ? 'CORE LOST' : 'CORE DESTROYED',
          isViewerLoss ? COLOR_ENEMY : COLOR_OWN,
          { riseDistPx: 90, durMs: 2400, fontSizePx: 38 },
        );
      }
    }
  }
}
